import { common, query } from '@kurtosys/ksys-app-template';
import { IFormatter } from '@kurtosys/types/data/IFormatter.js';

import { IGlobalConfiguration } from '../../../models/IGlobalConfiguration.js';
import { BarScaleCalculator } from '../../charting/BarScale/BarScaleCalculator.js';
import { ITooltipProps } from '../Tooltip/models/index.js';
import { ITranslationContextValue } from '../Translate/models/index.js';

import { ITableColumnDisplay, ITableGroup, ITableProps, TableColumnDisplayType } from './models/index.js';
import { ITableColumn } from './models/ITableColumn.js';
import { ITableGroupByOptions } from './models/ITableGroupByOptions.js';
import { ITableHeaderGroup } from './models/ITableHeaderGroup.js';
import { ITableHeaderMapping } from './models/ITableHeaderMapping.js';
import { ITableRow } from './models/ITableRow.js';
import { TTableGroupType } from './models/TTableGroupType.js';

export class TableLogicBase {
	public props: ITableProps;
	public globalConfiguration?: IGlobalConfiguration;
	public translationHelper?: common.helpers.TranslationHelper;

	protected BarScaleCalculator: BarScaleCalculator | undefined;

	constructor(
		props: ITableProps,
		globalConfiguration?: IGlobalConfiguration,
		translationHelper?: common.helpers.TranslationHelper,
	) {
		this.props = props;
		this.globalConfiguration = globalConfiguration;
		this.translationHelper = translationHelper;
		if (props.rows && props.columns) {
			const barScaleCol = props.columns.find(
				(col) => col.display && col.display.type === TableColumnDisplayType.barScale,
			);
			if (
				barScaleCol &&
				barScaleCol.display &&
				barScaleCol.display.options &&
				barScaleCol.display.options.barScale
			) {
				const { baseOptions } = barScaleCol.display.options.barScale;
				this.BarScaleCalculator = new BarScaleCalculator(
					props.rows,
					(row) => this.getRawValue(row, barScaleCol),
					baseOptions,
				);
			}
		}
	}
	getRawValue(row: object, column: ITableColumn) {
		const { key, keyQuery } = column;
		let value = common.commonUtils.get(row, key);
		if (keyQuery) {
			const queryInstance = new query.Query(keyQuery);
			value = queryInstance.execute({ responseContext: row }) || value;
		}

		if (value === undefined && this.translationHelper) {
			const translatedKey = this.translationHelper.translate(key);
			value = common.commonUtils.get(row, translatedKey);
		}
		return value;
	}
	formatValueForColumn = (value: any, column: ITableColumn, translationContext?: ITranslationContextValue) => {
		const { formats: globalFormats = {} } = this.globalConfiguration || {};
		const { formatsByType = {} } = this.props;
		const { formatType, format } = column;
		const workingFormat = format || (formatType && (formatsByType[formatType] || globalFormats[formatType]));
		if (workingFormat && !common.commonUtils.isNullOrUndefined(value)) {
			if (!workingFormat.locale) {
				workingFormat.locale = translationContext && translationContext.culture;
			}
			if (!workingFormat.options) {
				workingFormat.options = {};
			}
			return common.commonUtils.formatter(workingFormat as IFormatter, value);
		}
		return value;
	};
	getCellSuperscripts = (column: ITableColumn, row: ITableRow): (string | number)[] | undefined => {
		const { id } = column;
		const { _superscriptsByColumnId } = row;
		if (_superscriptsByColumnId && _superscriptsByColumnId[id]) {
			return _superscriptsByColumnId[id].sort((a, b) => {
				return a === b ? 0 : a > b ? 1 : -1;
			});
		}
		return undefined;
	};
	getHeadCellSuperscripts = (column: ITableColumn | ITableHeaderGroup): (string | number)[] | undefined => {
		const { _headerSuperscripts } = column;
		if (_headerSuperscripts) {
			return _headerSuperscripts;
		}
		return undefined;
	};

	get emptyColumnIds(): string[] {
		const response: string[] = [];
		const { columns, rows } = this.props;
		if (columns && rows) {
			for (const column of columns.filter((column) => !column.hide)) {
				let columnHasData = false;
				for (const row of rows) {
					const value = this.getRawValue(row, column);
					if (!common.commonUtils.isNullOrEmpty(value)) {
						columnHasData = true;
						break;
					}
				}
				if (!columnHasData) {
					response.push(column.id);
				}
			}
		}
		return response;
	}
	get visibleColumns(): ITableColumn[] {
		const { columns, excludeEmptyColumns } = this.props;
		const emptyColumnIds = this.emptyColumnIds;
		return (columns || []).filter((column) => {
			const { id, hide } = column;
			return !hide && (!excludeEmptyColumns || !emptyColumnIds.includes(id));
		});
	}

	get headerColumns(): ITableColumn[] {
		const columns = [...this.visibleColumns];
		if (!columns.some((column) => column.id === '_expander')) {
			if (this.props && this.grouping) {
				const { alignExpander = 'left' } = this.grouping;
				if (alignExpander === 'left') {
					columns.unshift(this.getExpanderColumn(false));
				} else {
					columns.push(this.getExpanderColumn(false));
				}
			}
		}
		return columns;
	}

	public getExpanderColumn = (hasChildren: boolean): ITableColumn => {
		const response: ITableColumn = {
			id: '_expander',
			key: '_expander',
			hide: false,
			excludeFromNoDataCheck: true,
			placeholder: ' ',
		};
		if (hasChildren) {
			response.display = {
				type: TableColumnDisplayType.expander,
				options: {
					expander: {},
				},
			};
		}
		return response;
	};

	get defaultHeaderGroup(): ITableHeaderGroup {
		return {
			id: '_defaultHeaderGroup',
			header: '',
		};
	}
	get headerMapping(): ITableHeaderMapping[][] {
		const headRows: ITableHeaderMapping[][] = [];

		const columns = this.headerColumns;
		const { headerGroups } = this.props;

		const headerGroupsById = common.commonUtils.pivotCollection(
			headerGroups || [],
			(value: ITableHeaderGroup) => value.id,
		);
		const headerRowCount = this.getHeaderRowCount(columns, headerGroupsById);
		for (let counter = 0; counter < headerRowCount; counter++) {
			headRows.push([]);
		}
		let activeSpan = 0;
		for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
			const column = columns[columnIndex];
			const { headerGroupId, headerColSpan, colSpan = 1 } = column;
			/**
			 * Skip over the current column if it falls into the colSpan from a previous column
			 */
			if (activeSpan > 1) {
				activeSpan--;
				continue;
			}
			activeSpan = headerColSpan || colSpan;
			const headerGroup = !!headerGroupId && headerGroupsById[headerGroupId];
			if (!headerGroup) {
				headRows[0].push({
					column,
					columnIndex,
					colSpan: headerColSpan || colSpan,
					rowSpan: 1,
					hasChildren: false,
				});
			} else {
				const parentIndex = this.loadGroupInHeadRows(headerGroup, headRows, headerGroupsById);
				const rowIndex = parentIndex + 1;
				const row = headRows && headRows.length > rowIndex && headRows[rowIndex];
				if (row) {
					row.push({
						column,
						columnIndex,
						colSpan: headerColSpan || colSpan,
						rowSpan: 1,
						hasChildren: false,
					});
				}
			}
		}
		for (let index = 0; index < headRows.length; index++) {
			const row = headRows[index];
			const remainingRowCount = headRows.length - index;
			for (const mapping of row) {
				if (!mapping.hasChildren) {
					mapping.rowSpan = remainingRowCount;
				}
			}
		}
		return headRows;
	}

	getHeaderRowCount = (columns: ITableColumn[], headerGroupsById: { [key: string]: ITableHeaderGroup }) => {
		let max = 1;
		for (const column of columns) {
			const { headerGroupId } = column;
			const headerGroup = !!headerGroupId && headerGroupsById[headerGroupId];
			if (headerGroup) {
				const groupNesting = this.getGroupNesting(headerGroup, headerGroupsById);
				max = Math.max(max, groupNesting);
			}
		}
		return max;
	};

	getGroupNesting = (
		headerGroup: ITableHeaderGroup,
		headerGroupsById: { [key: string]: ITableHeaderGroup },
		seed = 1,
	): number => {
		seed++;
		const { parentId } = headerGroup;
		const parentHeaderGroup = !!parentId && headerGroupsById[parentId];
		if (parentHeaderGroup) {
			return this.getGroupNesting(parentHeaderGroup, headerGroupsById, seed);
		}
		return seed;
	};

	loadGroupInHeadRows = (
		group: ITableHeaderGroup,
		headRows: ITableHeaderMapping[][],
		headerGroupsById: { [key: string]: ITableHeaderGroup },
	) => {
		let index = 0;
		const parentGroupId = group.parentId;
		const parentGroup = !!parentGroupId && headerGroupsById[parentGroupId];
		if (parentGroup) {
			index = this.loadGroupInHeadRows(parentGroup, headRows, headerGroupsById) + 1;
		}
		const row = headRows && headRows.length > index && headRows[index];
		if (row) {
			const existing = row.find((mapping) => mapping.group && mapping.group.id === group.id);
			if (existing) {
				existing.colSpan++;
			} else {
				row.push({
					group,
					colSpan: 1,
					rowSpan: 1,
					hasChildren: true,
				});
			}
		}
		return index;
	};

	getColumnDisplay(column: ITableColumn): ITableColumnDisplay {
		let response: ITableColumnDisplay = {
			type: TableColumnDisplayType.raw,
		};
		if (column.display) {
			response = column.display;
		} else if (column.contextMenuProps) {
			response = {
				type: TableColumnDisplayType.contextMenu,
				options: {
					contextMenu: column.contextMenuProps,
				},
			};
		} else if (column.documentContextMenuProps) {
			response = {
				type: TableColumnDisplayType.documentContextMenu,
				options: {
					documentContextMenu: column.documentContextMenuProps,
				},
			};
		} else if (column.actionsProps) {
			response = {
				type: TableColumnDisplayType.actions,
				options: {
					actions: column.actionsProps,
				},
			};
		} else if (column.checkboxProps) {
			response = {
				type: TableColumnDisplayType.checkbox,
				options: {
					checkbox: column.checkboxProps,
				},
			};
		} else if (column.iconProps) {
			response = {
				type: TableColumnDisplayType.icon,
				options: {
					icon: column.iconProps,
				},
			};
		} else if (column.changeIndicatorProps) {
			response = {
				type: TableColumnDisplayType.changeIndicator,
				options: {
					changeIndicator: column.changeIndicatorProps,
				},
			};
		}

		if (!response.type || !response.options || !response.options[response.type]) {
			response = {
				type: TableColumnDisplayType.raw,
			};
		}

		return response;
	}
	static getTooltipRowProps(row: ITableRow, column: ITableColumn): ITooltipProps[] {
		const response: ITooltipProps[] = [];
		if (column && Array.isArray(column.tooltips) && column.tooltips.length > 0) {
			const { showMultipleTooltips } = column;
			const tooltips = common.commonUtils.filterWithMatchingProperties(column.tooltips, { target: 'row' });
			if (tooltips) {
				for (let i = 0; i < tooltips.length; i++) {
					const tooltip = tooltips[i];
					if (tooltip) {
						let shouldAddTooltip = true;
						if (tooltip.conditional) {
							const conditionalTooltipHelper = new common.helpers.ConditionalHelper(
								{ queryClass: query.Query },
								tooltip.conditional,
							);
							shouldAddTooltip = conditionalTooltipHelper.matches(row);
						}
						if (shouldAddTooltip) {
							// eslint-disable-next-line @typescript-eslint/no-unused-vars
							const { target, conditional, ...tooltipProps } = tooltip;
							response.push(tooltipProps);
							if (!showMultipleTooltips) {
								break;
							}
						}
					}
				}
			}
		}
		if (response && response.length > 0 && row._tooltipInputs) {
			for (const tooltipProps of response) {
				if (tooltipProps.content) {
					tooltipProps.content = common.commonUtils.replacePlaceholders({
						values: row._tooltipInputs,
						text: tooltipProps.content,
					});
				}
			}
		}
		return response;
	}

	get grouping(): ITableGroup | null {
		return this.orchestrateGrouping(this.props, this.props.grouping);	
	}

	getGroupType(groupingConfig: ITableGroup | null): TTableGroupType {
		const defaultType: TTableGroupType = 'Custom';
		if (!groupingConfig) {
			return defaultType;
		}
		if (!groupingConfig.groupType) {
			return defaultType;
		}
		return groupingConfig.groupType;
	}
	
	getGroupOptions(groupType: TTableGroupType, groupingConfig: ITableGroup | null): ITableGroup | ITableGroupByOptions | undefined | null{
		if (!groupingConfig && !groupType) {
			return;
		}
		switch (groupType) {
			case 'GroupBy':
				return groupingConfig?.groupTypeOptions?.groupBy;
			case 'Custom':
				return groupingConfig;
			default:
				return;
		}
	}

	private orchestrateGrouping = <TRow extends ITableRow>(
		props: ITableProps<TRow>,
		grouping: ITableGroup | undefined,
		parentGrouping?: ITableGroup | undefined,
	): ITableGroup | null => {
		let currentGrouping: ITableGroup | undefined;
		const { queryStore } = this.props;

		if (grouping) {	
			const groupType = this.getGroupType(grouping);
			const groupOptions = this.getGroupOptions(groupType, grouping);
			if (grouping.disableTableGroupingConditional) {
				const disableTableGroupingConditional = grouping.disableTableGroupingConditional;
				const executionOptions = queryStore && queryStore.executionOptions;
				const conditionalHelper = new common.helpers.ConditionalHelper(
					{ queryClass: query.Query },
					disableTableGroupingConditional,
					this.translationHelper,
				);
				const disableGrouping = conditionalHelper.matches(props.rows, disableTableGroupingConditional, executionOptions);
				if (disableGrouping){
					return null
				}
			}
			
			currentGrouping = { ...grouping, columns: this.visibleColumns };
			switch (groupType) {
				case 'GroupBy': {
					const { groupTypeOptions } = grouping;
					if (!groupTypeOptions) {
						break;
					}
					const { groupBy } = groupTypeOptions;
					if (!groupBy) {
						break;
					}
					const { comparisonValue, ...commonOptions } = groupBy;
					currentGrouping = { ...currentGrouping, ...commonOptions };
					break;
				}
				case 'Custom':
				default:
					break;
			}
			if (!currentGrouping.id) {
				currentGrouping.id = common.commonUtils.generateGuid();
			}
			const { columnOverrides } = currentGrouping;
			if (!currentGrouping.columns && columnOverrides) {
				const columns: ITableColumn[] = [];
				for (let columnIndex = 0; columnIndex < this.visibleColumns.length; columnIndex++) {
					const column = this.visibleColumns[columnIndex];
					const columnOverride = columnOverrides[column.id];
					if (columnOverride) {
						columns.push(common.commonUtils.deepMergeObjects(column, columnOverride));
					}
				}
				currentGrouping.columns = columns;
			}

			// Pass down the orchestrateChildRows as we orchestrate the groupings
			if (!currentGrouping.orchestrateChildRows && parentGrouping && parentGrouping.orchestrateChildRows) {
				currentGrouping.orchestrateChildRows = parentGrouping.orchestrateChildRows;
			}

			// This group type should only apply one level of child grouping.
			if (groupType === 'GroupBy' && !parentGrouping) {
				const groupByOptions = groupOptions as ITableGroupByOptions;
				if (groupByOptions && groupByOptions.comparisonValue) {
					currentGrouping.fetchChildren = async <TRow extends ITableRow>(row: TRow, columns: ITableColumn[]) => {
						return row._groupByChildren || [];
					}
				}
			}

			const hasChildren = currentGrouping.getRowsQuery !== undefined || currentGrouping.fetchChildren !== undefined;

			// load the last grouping to handle the final columns having an expanded column
			if (!currentGrouping.childGroup && hasChildren) {
				currentGrouping.childGroup = {
					id: '_lastGroup',
					alignExpander: grouping.alignExpander,
				};
			}
			if (currentGrouping.childGroup) {
				if (!currentGrouping.childGroup.alignExpander) {
					currentGrouping.childGroup.alignExpander = grouping.alignExpander;
				}
				currentGrouping.childGroup = this.orchestrateGrouping(props, currentGrouping.childGroup, currentGrouping);
			}
			if (!currentGrouping.columns) {
				currentGrouping.columns = [...(props.columns || [])];
			}
			if (!currentGrouping.columns.some((column) => column.id === '_expander')) {
				const { alignExpander = 'left' } = grouping;
				if (alignExpander === 'left') {
					currentGrouping.columns.unshift(this.getExpanderColumn(!!currentGrouping.childGroup));
				} else {
					currentGrouping.columns.push(this.getExpanderColumn(!!currentGrouping.childGroup));
				}
			}
		}

		return currentGrouping || null;
	};
}
